import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  FileText, 
  Search, 
  Download, 
  Trash2, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  Building2,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Documents = () => {
  const { token } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, doc: null });
  
  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const fetchDocuments = useCallback(async () => {
    try {
      // Fetch all suppliers and their documents
      const suppliersRes = await axios.get(`${API}/suppliers?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const allDocs = [];
      for (const supplier of suppliersRes.data.suppliers) {
        const docsRes = await axios.get(`${API}/suppliers/${supplier.supplier_id}/documents`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        docsRes.data.forEach(doc => {
          allDocs.push({
            ...doc,
            company_name: supplier.company_name,
            supplier_id: supplier.supplier_id
          });
        });
      }
      
      setDocuments(allDocs);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleDelete = async () => {
    if (!deleteDialog.doc) return;
    
    try {
      await axios.delete(`${API}/documents/${deleteDialog.doc.document_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document deleted successfully');
      fetchDocuments();
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast.error('Failed to delete document');
    } finally {
      setDeleteDialog({ open: false, doc: null });
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API}/documents/${doc.document_id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download document');
    }
  };

  const getDocumentStatus = (doc) => {
    if (!doc.expiry_date) return { status: 'valid', label: 'No Expiry', icon: CheckCircle, color: 'text-green-500', badgeClass: 'status-active' };
    
    const expiry = new Date(doc.expiry_date);
    const now = new Date();
    const thirtyDays = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    
    if (expiry < now) {
      return { status: 'expired', label: 'Expired', icon: AlertTriangle, color: 'text-red-500', badgeClass: 'status-expired' };
    } else if (expiry < thirtyDays) {
      return { status: 'expiring', label: 'Expiring Soon', icon: Clock, color: 'text-orange-500', badgeClass: 'status-warning' };
    }
    return { status: 'valid', label: 'Valid', icon: CheckCircle, color: 'text-green-500', badgeClass: 'status-active' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      contract: 'Contract',
      insurance: 'Insurance',
      compliance: 'Compliance',
      accreditation: 'Accreditation',
      health_safety: 'Health & Safety',
      risk_assessment: 'Risk Assessment',
      method_statement: 'Method Statement',
      other: 'Other'
    };
    return labels[type] || type;
  };

  // Filter documents
  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = !search || 
      doc.original_filename.toLowerCase().includes(search.toLowerCase()) ||
      doc.company_name.toLowerCase().includes(search.toLowerCase());
    
    const docStatus = getDocumentStatus(doc);
    const matchesStatus = !statusFilter || statusFilter === 'all' || docStatus.status === statusFilter;
    const matchesType = !typeFilter || typeFilter === 'all' || doc.document_type === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const hasActiveFilters = search || statusFilter || typeFilter;

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setTypeFilter('');
  };

  // Stats
  const expiredCount = documents.filter(d => getDocumentStatus(d).status === 'expired').length;
  const expiringCount = documents.filter(d => getDocumentStatus(d).status === 'expiring').length;
  const validCount = documents.filter(d => getDocumentStatus(d).status === 'valid').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="documents-page">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-heading tracking-tight">Documents</h1>
        <p className="text-muted-foreground mt-1">
          Manage all supplier documents in one place
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="cursor-pointer hover:border-green-500/50 transition-colors" onClick={() => setStatusFilter('valid')}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-2 bg-green-500/10 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{validCount}</p>
              <p className="text-sm text-muted-foreground">Valid Documents</p>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:border-orange-500/50 transition-colors" onClick={() => setStatusFilter('expiring')}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-2 bg-orange-500/10 rounded-lg">
              <Clock className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{expiringCount}</p>
              <p className="text-sm text-muted-foreground">Expiring Soon</p>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:border-red-500/50 transition-colors" onClick={() => setStatusFilter('expired')}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-2 bg-red-500/10 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{expiredCount}</p>
              <p className="text-sm text-muted-foreground">Expired</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by filename or supplier..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full lg:w-[180px]" data-testid="status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="valid">Valid</SelectItem>
                <SelectItem value="expiring">Expiring Soon</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full lg:w-[180px]" data-testid="type-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="contract">Contract</SelectItem>
                <SelectItem value="insurance">Insurance</SelectItem>
                <SelectItem value="compliance">Compliance</SelectItem>
                <SelectItem value="accreditation">Accreditation</SelectItem>
                <SelectItem value="health_safety">Health & Safety</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button variant="ghost" onClick={clearFilters} className="shrink-0">
                <X className="w-4 h-4 mr-2" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Documents Table */}
      <Card>
        <CardContent className="p-0">
          {filteredDocuments.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium mb-2">No documents found</h3>
              <p className="text-muted-foreground">
                {hasActiveFilters ? 'Try adjusting your filters' : 'Documents will appear here when uploaded to suppliers'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="documents-table">
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Document</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Expiry Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[100px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDocuments.map((doc) => {
                    const docStatus = getDocumentStatus(doc);
                    const StatusIcon = docStatus.icon;
                    return (
                      <TableRow key={doc.document_id} data-testid={`doc-row-${doc.document_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-muted-foreground" />
                            <div>
                              <p className="font-medium truncate max-w-[200px]">{doc.original_filename}</p>
                              <p className="text-xs text-muted-foreground">
                                Uploaded {formatDate(doc.created_at)}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Link 
                            to={`/suppliers/${doc.supplier_id}`}
                            className="flex items-center gap-2 hover:text-primary"
                          >
                            <Building2 className="w-4 h-4" />
                            <span className="truncate max-w-[150px]">{doc.company_name}</span>
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{getDocumentTypeLabel(doc.document_type)}</Badge>
                        </TableCell>
                        <TableCell>
                          {doc.expiry_date ? formatDate(doc.expiry_date) : 'No expiry'}
                        </TableCell>
                        <TableCell>
                          <Badge className={docStatus.badgeClass}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {docStatus.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDownload(doc)}
                              data-testid={`download-${doc.document_id}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => setDeleteDialog({ open: true, doc })}
                              className="text-red-500 hover:text-red-600"
                              data-testid={`delete-${doc.document_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, doc: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteDialog.doc?.original_filename}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Documents;
